import { useCallback, useMemo } from "react";
import { useTable, Column } from "react-table";
import { filteredInspectionLinksTable, filteredInspectionTable } from "../../Functions";
import { TInspectionTask, TInspectionTaskLink, TTableProps } from "../../../types/types";
import usePagination from "../usePagination";

type UseInspectionTableProps = {
    tasks: TInspectionTask[];
    taskLinks: TInspectionTaskLink[];
    taskColumns: Column<TInspectionTask>[];
    linkColumns: Column<TInspectionTaskLink>[];
    taskSearchTerm: string;
    linkSearchTerm: string;
    currentActiveInspectionTask: { original: TInspectionTask } | null;
    setCurrentActiveInspectionTask: React.Dispatch<React.SetStateAction<{ original: TInspectionTask } | null>>;
    pageSize?: number;
};

/**
 * Hook for managing inspection task tables and pagination
 * Handles table instances, filtering, and row selection
 */
export function useInspectionTable({
    tasks,
    taskLinks,
    taskColumns,
    linkColumns,
    taskSearchTerm,
    linkSearchTerm,
    currentActiveInspectionTask,
    setCurrentActiveInspectionTask,
    pageSize = 8
}: UseInspectionTableProps) {

    // Row click handlers
    const onTaskClick = useCallback(
        (row: { original: TInspectionTask }) => setCurrentActiveInspectionTask(row),
        [setCurrentActiveInspectionTask]
    );

    const onTaskLinkClick = useCallback((row: { original: TInspectionTaskLink }) => {
        if (row?.original?.linkUrl) {
            window.open(row.original.linkUrl, '_blank');
        }
    }, []);

    // Task table instance
    const taskTableInstance = useTable({
        columns: taskColumns,
        data: tasks,
        autoResetHiddenColumns: false,
        autoResetSortBy: false,
    });

    // Data version key for detecting content changes (not just add/remove)
    const dataVersion = useMemo(() =>
        JSON.stringify(tasks.map(t => ({
            id: t.inspectionTaskId,
            h: t.taskHeader,
            d: t.taskDescription,
            o: t.listOrder
        }))),
        [tasks]
    );

    // Task table data with row click handler
    // Note: dataVersion is included in deps to force recalculation when task content changes,
    // even though react-table's row references remain stable. This is intentional.
    const inspectionTaskTableData: TTableProps<TInspectionTask> = useMemo(
        () => ({
            onRowClick: onTaskClick,
            selectedRow: currentActiveInspectionTask,
            ...taskTableInstance,
            _dataVersion: dataVersion // Include to satisfy ESLint and document the dependency
        }),
        [dataVersion, taskTableInstance, currentActiveInspectionTask, onTaskClick]
    );

    // Filtered and paginated task rows
    const filteredTaskRows = useMemo(
        () => filteredInspectionTable(taskSearchTerm, inspectionTaskTableData.rows) || inspectionTaskTableData.rows,
        [taskSearchTerm, inspectionTaskTableData.rows]
    );

    const taskPagination = usePagination(filteredTaskRows, pageSize);

    // Links table instance
    const linksTableInstance = useTable({
        columns: linkColumns,
        data: taskLinks,
        autoResetHiddenColumns: false,
        autoResetSortBy: false,
    });

    // Data version key for detecting link content changes (not just add/remove)
    const linkDataVersion = useMemo(() =>
        JSON.stringify(taskLinks.map(l => ({
            id: l.itLinkId,
            url: l.linkUrl,
            text: l.linkText,
            cat: l.linkCategory
        }))),
        [taskLinks]
    );

    // Note: linkDataVersion forces recalculation when link content changes (see comment above)
    const inspectionTaskLinksData: TTableProps<TInspectionTaskLink> = useMemo(
        () => ({ 
            onRowClick: onTaskLinkClick, 
            ...linksTableInstance,
            _dataVersion: linkDataVersion // Include to satisfy ESLint and document the dependency
        }),
        [linkDataVersion, linksTableInstance, onTaskLinkClick]
    );

    // Filtered and paginated link rows
    const filteredLinkRows = useMemo(
        () => filteredInspectionLinksTable(linkSearchTerm, inspectionTaskLinksData.rows) || inspectionTaskLinksData.rows,
        [linkSearchTerm, inspectionTaskLinksData.rows]
    );

    const linkPagination = usePagination(filteredLinkRows, pageSize);

    return {
        inspectionTaskTableData,
        taskPagination,
        inspectionTaskLinksData,
        linkPagination
    };
}
